import { useId } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { PortalRole } from '../../lib/types'
import { Button } from '../ui/Button'

export type PortalAccessConfirm =
  | { mode: 'grant'; email: string; role: PortalRole }
  | { mode: 'revoke'; email: string }

const COPY = {
  grant: {
    title: 'settings.access.confirm.grantTitle',
    text: 'settings.access.confirm.grantText',
    button: 'settings.access.confirm.grantButton',
  },
  revoke: {
    title: 'settings.access.confirm.revokeTitle',
    text: 'settings.access.confirm.revokeText',
    button: 'settings.access.confirm.revokeButton',
  },
} as const

/** Settings › Access's confirmation (MS-997 D7) — the prototype's
 * `#portal-access-confirm-modal`: one plain dialogue, a title and one
 * paragraph with the email (and, for a grant, the role) in bold, Cancel and
 * a primary confirm. No authorisation checkbox or summary table: only an
 * Admin reaches this list. The backdrop closes it, as Cancel does. */
export function PortalAccessConfirmModal({
  confirm,
  onConfirm,
  onCancel,
}: Readonly<{
  confirm: PortalAccessConfirm
  onConfirm: () => void
  onCancel: () => void
}>) {
  const { t } = useTranslation()
  const titleId = useId()
  const copy = COPY[confirm.mode]
  const role = confirm.mode === 'grant' ? t(`settings.roles.${confirm.role}`) : undefined

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[1000]">
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="portal-access-confirm-backdrop"
        className="absolute inset-0 bg-black/40 cursor-default"
        onClick={onCancel}
      />
      <dialog
        open
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative m-0 p-0 border-0 text-inherit bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow-lg)] w-[420px] max-w-[90vw] overflow-hidden"
      >
        <div className="px-6 pt-5 pb-4 border-b border-[var(--border-light)]">
          <h3 id={titleId} className="text-[16px] font-semibold text-[var(--text-primary)]">
            {t(copy.title)}
          </h3>
        </div>
        <div className="px-6 py-5">
          <p className="m-0 text-[13px] leading-[1.6] text-[var(--text-secondary)]">
            <Trans i18nKey={copy.text} values={{ email: confirm.email, role }} components={{ strong: <strong /> }} />
          </p>
        </div>
        <div className="px-6 py-4 border-t border-[var(--border-light)] flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('settings.access.confirm.cancel')}
          </Button>
          <Button type="button" variant="primary" onClick={onConfirm}>
            {t(copy.button)}
          </Button>
        </div>
      </dialog>
    </div>
  )
}
